/**
 * DailyDashboard — Three-mode daily planning view.
 * Mode 1: No plan today — CTA to start planning
 * Mode 2: Active plan — action checklist with checkoffs
 * Mode 3: Planning/Review conversation — streaming chat
 */

import { useState, useEffect, useCallback } from 'react';
import { Sun, Moon, Plus, Loader2, CalendarCheck, Zap, Compass, X, PartyPopper, Sparkles } from 'lucide-react';
import {
  getDailyPlan, startPlanning, startReview, startCheckIn,
  updateDailyAction, addDailyAction, deleteDailyAction,
  acknowledgeDriveLevel
} from '../lib/api';
import ActionItem from './ActionItem';
import FollowThroughRing from './FollowThroughRing';
import PlanningChat from './PlanningChat';
import OnboardingChat from './OnboardingChat';
import EntrepreneurOnboarding from './EntrepreneurOnboarding';
import StartTrialGate from './StartTrialGate';
import UpgradePrompt from './UpgradePrompt';
import NavigatorInsight from './NavigatorInsight';
import ContinuityStrip from './ContinuityStrip';
import DeepRestCard from './DeepRestCard';
import DayArc from './DayArc';
import { listRestSessions } from '../lib/api';
import { trackAuthed } from '../lib/analytics';
import { useHints, useNudge } from '../lib/hints';
import { useRestPlayer } from '../lib/restPlayer';
import Nudge from './Nudge';
import Skeleton, { SkeletonAction } from './Skeleton';

function FreeWeekBanner({ daysLeft, onStartTrial }) {
  const label = daysLeft === 1 ? '1 day left' : `${daysLeft} days left`;
  return (
    <div className="bg-blue-900/20 border border-blue-800/40 rounded-lg p-4 mb-4">
      <div className="flex items-start gap-3">
        <Sparkles size={16} className="text-blue-400 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-slate-200 font-medium mb-1">Free planning week: {label}</p>
          <p className="text-xs text-slate-400 mb-3">
            Coaching sessions, evening review and full memory unlock with the 14-day trial. No charge today.
          </p>
          <button
            onClick={onStartTrial}
            className="bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium rounded-md px-3 py-1.5 transition-colors"
          >
            Start the trial
          </button>
        </div>
      </div>
    </div>
  );
}

function DriveLevelCalibrationBanner({ onDismiss, onNavigate }) {
  const [busy, setBusy] = useState(false);

  const dismiss = async () => {
    if (busy) return;
    setBusy(true);
    try { await acknowledgeDriveLevel(); } catch { /* swallow — local state still hides it */ }
    onDismiss?.();
  };

  const goToSettings = async () => {
    if (busy) return;
    setBusy(true);
    try { await acknowledgeDriveLevel(); } catch { /* still navigate */ }
    onDismiss?.();
    onNavigate?.('settings');
  };

  return (
    <div className="bg-emerald-900/20 border border-emerald-800/40 rounded-lg p-4 mb-4">
      <div className="flex items-start gap-3">
        <Compass size={16} className="text-emerald-400 mt-0.5 flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm text-slate-200 font-medium mb-1">How did that feel?</p>
          <p className="text-xs text-slate-400 mb-3">
            You can dial the Navigator's intensity up or down anytime — Coach (lighter), Mentor (default), or Sparring Partner (heavier). Same honesty at every level.
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={goToSettings}
              disabled={busy}
              className="bg-emerald-700 hover:bg-emerald-600 disabled:bg-slate-700 text-white text-xs font-medium rounded-md px-3 py-1.5 transition-colors"
            >
              Take me to Settings
            </button>
            <button
              onClick={dismiss}
              disabled={busy}
              className="text-xs text-slate-400 hover:text-slate-200 transition-colors px-2 py-1.5"
            >
              Got it
            </button>
          </div>
        </div>
        <button
          onClick={dismiss}
          disabled={busy}
          aria-label="Dismiss"
          className="text-slate-500 hover:text-slate-300 transition-colors"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
}

// Sparkle positions/delays are fixed (deterministic) so the burst reads as
// designed rather than random. Motion is neutralized under prefers-reduced-motion.
const WIN_SPARKLES = [
  { left: '12%', delay: '0ms' },
  { left: '28%', delay: '120ms' },
  { left: '44%', delay: '60ms' },
  { left: '60%', delay: '180ms' },
  { left: '76%', delay: '40ms' },
  { left: '88%', delay: '150ms' },
];

function WinCelebration({ completionPct }) {
  const strong = completionPct >= 80;
  return (
    <div
      className={`relative overflow-hidden rounded-lg px-4 py-3.5 text-center animate-win-pop ${
        strong
          ? 'bg-green-950/40 border border-green-700/50'
          : 'bg-green-950/30 border border-green-800/40'
      }`}
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 h-6">
        {WIN_SPARKLES.map((s, i) => (
          <span
            key={i}
            className="absolute top-2 w-1.5 h-1.5 rounded-full bg-green-300 animate-sparkle-rise"
            style={{ left: s.left, animationDelay: s.delay }}
          />
        ))}
      </div>
      <div className="relative flex items-center justify-center gap-2">
        <PartyPopper size={16} className="text-green-400 animate-check-pop" />
        <p className="text-sm font-medium text-green-300">
          {strong ? '80%+ — real momentum.' : "Commitments locked in. You've won the day."}
        </p>
      </div>
    </div>
  );
}

export default function DailyDashboard({ subscription, userState, user, sessions, activeAssignment, insights, patterns, assignments, onDataChange, onNavigate }) {
  const [planData, setPlanData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null); // action id being toggled
  const [chatMode, setChatMode] = useState(null); // 'planning' | 'review' | null
  const [chatSessionId, setChatSessionId] = useState(null);
  const [chatOpenerText, setChatOpenerText] = useState(null);
  const [startingPlan, setStartingPlan] = useState(false);
  const [addingAction, setAddingAction] = useState(false);
  const [newActionText, setNewActionText] = useState('');
  const [limitError, setLimitError] = useState(null);
  const [driveCalibrationDismissed, setDriveCalibrationDismissed] = useState(false);
  const [showTrialGate, setShowTrialGate] = useState(false);
  const [restToday, setRestToday] = useState({ prime: false, rest: false });
  const hints = useHints();
  const player = useRestPlayer();
  const [primeNudgeOn, setPrimeNudgeOn] = useState(false);
  const primeNudge = useNudge('plan_prime', primeNudgeOn);
  const showDriveCalibration = !driveCalibrationDismissed && !!user?.drive_level_calibration_pending;
  const driveLevel = user?.drive_level || 'mentor';
  // Locked-out = no active/trialing paid subscription (backend tier 'free').
  // Such a user gets a free week of daily planning after onboarding
  // (subscription.free_planning, backend tiers.FREE_PLANNING_DAYS); coaching,
  // check-ins and evening review stay walled by the API throughout.
  const isPaid = subscription?.tier === 'pro' || subscription?.tier === 'entrepreneur';
  const lockedOut = !!subscription && !isPaid;
  const freePlanning = subscription?.free_planning;
  const freeWeekActive = lockedOut && !!freePlanning?.active;

  const loadPlan = useCallback(async () => {
    try {
      const data = await getDailyPlan();
      setPlanData(data);
    } catch (err) {
      console.error('Failed to load daily plan:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPlan();
  }, [loadPlan]);

  // Which Deep Rest steps are done today (for the day arc). Cheap: one list call.
  const loadRestToday = useCallback(async () => {
    try {
      const res = await listRestSessions({ limit: 6 });
      if (res.error || !Array.isArray(res.sessions)) return;
      const today = new Date().toISOString().slice(0, 10);
      const isToday = (x) => String(x.created_at || '').slice(0, 10) === today && x.status === 'ready';
      setRestToday({
        prime: res.sessions.some(x => x.kind === 'prime' && isToday(x)),
        rest: res.sessions.some(x => x.kind === 'rest' && isToday(x)),
      });
    } catch { /* leave as is */ }
  }, []);
  useEffect(() => { loadRestToday(); }, [loadRestToday]);

  // First-run tour: once, on the first Today after onboarding, never over
  // a chat or while audio plays. Replayable from You, "Show me around".
  const tourEligible = !!userState?.intake_completed && !chatMode && !loading && !lockedOut && !!hints?.hydrated && !hints.hasSeen('tour') && !player?.playing;
  useEffect(() => {
    if (!tourEligible || hints?.tourOpen) return undefined;
    const t = setTimeout(() => hints.startTour(), 1200);
    return () => clearTimeout(t);
  }, [tourEligible]); // eslint-disable-line react-hooks/exhaustive-deps


  const handleStartPlanning = async () => {
    setStartingPlan(true);
    try {
      const res = await startPlanning();
      if (res.error) {
        setLimitError(res);
        return;
      }
      if (res.session_id) {
        trackAuthed('plan_start', { resumed: !!res.resumed });
        setChatSessionId(res.session_id);
        setChatOpenerText(res.opener_text || null);
        setChatMode('planning');
      }
    } catch (err) {
      console.error('Failed to start planning:', err);
    } finally {
      setStartingPlan(false);
    }
  };

  const handleStartReview = async () => {
    try {
      const res = await startReview();
      if (res.error) {
        setLimitError(res);
        return;
      }
      if (res.session_id) {
        setChatSessionId(res.session_id);
        setChatOpenerText(res.opener_text || null);
        setChatMode('review');
      }
    } catch (err) {
      console.error('Failed to start review:', err);
    }
  };

  const handleStartCheckIn = async () => {
    try {
      const res = await startCheckIn();
      if (res.error) {
        setLimitError(res);
        return;
      }
      if (res.session_id) {
        setChatSessionId(res.session_id);
        setChatOpenerText(res.opener_text || null);
        setChatMode('check_in');
      }
    } catch (err) {
      console.error('Failed to start check-in:', err);
    }
  };

  const handleChatComplete = () => {
    if (chatMode === 'planning') { trackAuthed('plan_complete', {}); setPrimeNudgeOn(true); }
    if (chatMode === 'review') trackAuthed('review_complete', {});
    setChatMode(null);
    setChatSessionId(null);
    setChatOpenerText(null);
    setLoading(true);
    loadPlan();
  };

  const handleToggleAction = async (actionId, newStatus) => {
    setActionLoading(actionId);
    try {
      await updateDailyAction(actionId, { status: newStatus });
      // Update local state
      setPlanData(prev => {
        if (!prev) return prev;
        const updatedActions = prev.actions.map(a =>
          a.id === actionId
            ? { ...a, status: newStatus, completed_at: newStatus === 'completed' ? new Date().toISOString() : null }
            : a
        );
        const completed = updatedActions.filter(a => a.status === 'completed').length;
        const total = updatedActions.length;
        const rate = total > 0 ? Math.round((completed / total) * 100 * 100) / 100 : 0;
        return {
          ...prev,
          actions: updatedActions,
          plan: prev.plan ? { ...prev.plan, actions_completed: completed, completion_rate: rate } : prev.plan,
          streak: {
            ...prev.streak,
            total_actions_completed: (prev.streak?.total_actions_completed || 0) + (newStatus === 'completed' ? 1 : 0)
          }
        };
      });
    } catch (err) {
      console.error('Failed to toggle action:', err);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteAction = async (actionId) => {
    try {
      await deleteDailyAction(actionId);
      setPlanData(prev => {
        if (!prev) return prev;
        const updatedActions = prev.actions.filter(a => a.id !== actionId);
        return { ...prev, actions: updatedActions };
      });
    } catch (err) {
      console.error('Failed to delete action:', err);
    }
  };

  const handleAddAction = async () => {
    const text = newActionText.trim();
    if (!text) return;
    try {
      const res = await addDailyAction({ action_text: text });
      if (res.limit_type) {
        setLimitError(res);
        setAddingAction(false);
        setNewActionText('');
        return;
      }
      if (res.action) {
        setPlanData(prev => ({
          ...prev,
          actions: [...(prev?.actions || []), res.action]
        }));
        setNewActionText('');
        setAddingAction(false);
      }
    } catch (err) {
      console.error('Failed to add action:', err);
    }
  };

  // Onboarding gate: new users must complete onboarding first.
  // Voice is enabled for all tiers during onboarding so free users get a
  // taste of the feature; the backend mirrors this gate on /voice/speak
  // and /voice/transcribe (intake_completed=false bypasses the tier check).
  // Voice mode defaults OFF here, unconditionally — even for users whose saved
  // voice_default_on is true. The opening turn (and any fresh-session path that
  // runs StateCheckIn) auto-sends "I'm ready" with no user gesture, and iOS
  // blocks gestureless audio with a NotAllowedError. A prominent in-chat toggle
  // handles discovery, and tapping it counts as the gesture iOS requires. The
  // persisted preference still governs the main app, where every send is a tap.
  if (userState && !userState.intake_completed) {
    return <OnboardingChat
      onComplete={() => onDataChange?.()}
      voiceEnabled={true}
      voiceId={user?.voice_id}
      voiceDefaultOn={false}
      freePlanningDays={lockedOut ? (subscription?.limits?.free_planning_days || 7) : null}
    />;
  }

  // Trial wall. Onboarding above is free (the hook), and so is daily planning
  // for the week after it (the taste). The wall renders here only once that
  // week is over, or when the user taps "Start the trial" on the banner.
  // Before migration 035 ships, free_planning is absent and this falls back to
  // the old always-wall behaviour. Settings/Progress/Context stay reachable.
  if (userState?.intake_completed && lockedOut && (showTrialGate || !freeWeekActive)) {
    return (
      <StartTrialGate
        user={user}
        subscription={subscription}
        reason={freeWeekActive ? null : 'planning_week_over'}
        onBack={freeWeekActive ? () => setShowTrialGate(false) : null}
      />
    );
  }

  // Entrepreneur onboarding gate: entrepreneur users must set up spaces
  if (
    subscription?.tier === 'entrepreneur' &&
    userState?.intake_completed &&
    userState?.entrepreneur_onboarding_completed === false
  ) {
    return <EntrepreneurOnboarding
      onComplete={() => onDataChange?.()}
      voiceId={user?.voice_id}
      voiceDefaultOn={user?.voice_default_on !== false}
    />;
  }

  // Mode 3: Planning/Review chat
  if (chatMode) {
    return (
      <PlanningChat
        sessionId={chatSessionId}
        mode={chatMode}
        openerText={chatOpenerText}
        onComplete={handleChatComplete}
        voiceEnabled={subscription?.tier === 'pro' || subscription?.tier === 'entrepreneur'}
        voiceId={user?.voice_id}
        voiceDefaultOn={user?.voice_default_on !== false}
      />
    );
  }

  if (loading) {
    return (
      <div className="h-full overflow-auto">
        <div className="max-w-xl mx-auto px-4 py-6 space-y-4 animate-fade-in" aria-busy="true" aria-label="Loading today's plan">
          <div className="flex items-center justify-between">
            <Skeleton className="w-32 h-4" />
            <Skeleton className="w-20 h-3" />
          </div>
          <Skeleton className="w-full h-12" />
          <Skeleton className="w-full h-16" />
          <div className="space-y-2 pt-2">
            <Skeleton className="w-24 h-3" />
            <SkeletonAction />
            <SkeletonAction />
            <SkeletonAction />
          </div>
        </div>
      </div>
    );
  }

  const plan = planData?.plan;
  const actions = planData?.actions || [];
  // Deep Rest kind that fits the moment: Prime while the plan is fresh, Rest
  // once the day is reviewed, Reset when there is no plan yet.
  const planReviewed = plan?.status === 'reviewed';
  const planActive = !!plan && plan.status !== 'reviewed';
  const momentKind = planReviewed ? 'rest' : planActive ? 'prime' : 'reset';
  const arcState = {
    plan: !!plan && plan.status !== 'planning',
    prime: restToday.prime,
    do: (actions.filter(a => a.status === 'completed').length > 0) && actions.length > 0 && actions.every(a => a.status !== 'pending'),
    review: planReviewed,
    rest: restToday.rest,
  };
  const arcCurrent = !plan ? 'plan' : planReviewed ? (restToday.rest ? null : 'rest') : (restToday.prime ? 'do' : 'prime');
  const handleArcTap = (step) => {
    if (step === 'plan' && !plan) return handleStartPlanning();
    if (step === 'review' && planActive && !lockedOut) return handleStartReview();
    const el = document.getElementById(step === 'prime' || step === 'rest' ? 'deep-rest-card' : 'today-actions');
    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    return undefined;
  };
  const streak = planData?.streak;
  const yesterday = planData?.yesterday;

  // Mode 1: No plan today
  if (!plan || plan.status === 'planning') {
    return (
      <div className="h-full overflow-auto">
        <div className="max-w-xl mx-auto px-4 py-12">
          {showDriveCalibration && (
            <DriveLevelCalibrationBanner
              onDismiss={() => setDriveCalibrationDismissed(true)}
              onNavigate={onNavigate}
            />
          )}

          {freeWeekActive && (
            <FreeWeekBanner daysLeft={freePlanning.days_left} onStartTrial={() => setShowTrialGate(true)} />
          )}

          <DayArc state={arcState} current={arcCurrent} onTap={handleArcTap} className="mb-5" />

          <ContinuityStrip sessions={sessions} activeAssignment={activeAssignment} driveLevel={driveLevel} className="mb-6" />

          <div className="text-center mb-8">
            <Sun size={36} className="text-amber-400 mx-auto mb-3" />
            <h1 className="text-xl font-display font-semibold tracking-tight text-slate-200 mb-2">Ready to plan your day?</h1>
            <p className="text-sm text-slate-500">
              5 minutes to map out what matters. The Navigator will walk you through it.
            </p>
          </div>

          <FollowThroughRing streak={streak} assignments={assignments} />

          {/* Yesterday's stats */}
          {yesterday && (
            <div className="mt-4 bg-slate-800/40 border border-slate-700/40 rounded-lg p-4">
              <p className="text-xs text-slate-500 font-medium uppercase tracking-wide mb-2">Yesterday</p>
              <div className="flex items-center gap-4">
                <div>
                  <span className="text-lg font-semibold text-slate-200">{Math.round(yesterday.completion_rate || 0)}%</span>
                  <span className="text-xs text-slate-500 ml-1">completed</span>
                </div>
                <div className="text-xs text-slate-500">
                  {yesterday.actions_completed}/{yesterday.actions_planned} actions
                </div>
              </div>
            </div>
          )}

          <button
            id="start-planning"
            onClick={handleStartPlanning}
            disabled={startingPlan}
            className="w-full mt-6 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 text-white rounded-lg py-3.5 text-sm font-medium transition-colors flex items-center justify-center gap-2"
          >
            {startingPlan ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <>
                <Sun size={16} />
                Start Planning
              </>
            )}
          </button>

          {/* Check-ins are coaching sessions (API-walled for locked-out users) */}
          {!lockedOut && (
            <button
              onClick={handleStartCheckIn}
              className="w-full mt-3 bg-emerald-900/30 hover:bg-emerald-900/50 border border-emerald-800/40 text-emerald-300 rounded-lg py-2.5 text-xs font-medium transition-colors flex items-center justify-center gap-2"
            >
              <Zap size={14} />
              Or a quick check-in (60-90 sec)
            </button>
          )}

          {limitError && (
            <div className="mt-4">
              <UpgradePrompt limitError={limitError} onDismiss={() => setLimitError(null)} />
            </div>
          )}

          <div id="deep-rest-card">
            <DeepRestCard kind={momentKind} canCreate={isPaid} className="mt-6" onSessionsLoaded={loadRestToday} />
          </div>

          <NavigatorInsight insights={insights} patterns={patterns} currentRead={userState?.current_read} driveLevel={driveLevel} className="mt-6" />
        </div>
      </div>
    );
  }

  // Mode 2: Active plan — checklist view
  const committed = actions.filter(a => a.is_committed !== false);
  const momentum = actions.filter(a => a.is_committed === false);
  const committedDone = committed.filter(a => a.status === 'completed').length;
  const momentumDone = momentum.filter(a => a.status === 'completed').length;
  const completedCount = committedDone + momentumDone;
  const totalCount = actions.length;
  const completionPct = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;
  const committedPct = committed.length > 0 ? Math.round((committedDone / committed.length) * 100) : 0;
  const allCommittedDone = committed.length > 0 && committedDone === committed.length;

  return (
    <div className="h-full overflow-auto">
      <div className="max-w-xl mx-auto px-4 py-6 space-y-4">
        {showDriveCalibration && (
          <DriveLevelCalibrationBanner
            onDismiss={() => setDriveCalibrationDismissed(true)}
            onNavigate={onNavigate}
          />
        )}

        {freeWeekActive && (
          <FreeWeekBanner daysLeft={freePlanning.days_left} onStartTrial={() => setShowTrialGate(true)} />
        )}

        <DayArc state={arcState} current={arcCurrent} onTap={handleArcTap} className="mb-1" />

        {/* Continuity — the Navigator's memory of you, before you act */}
        <ContinuityStrip sessions={sessions} activeAssignment={activeAssignment} driveLevel={driveLevel} />

        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CalendarCheck size={18} className="text-blue-400" />
            <h2 className="text-sm font-medium text-slate-200">Today's Plan</h2>
          </div>
          <span className="text-xs text-slate-500">{planData?.today}</span>
        </div>

        {/* Streak bar */}
        <FollowThroughRing streak={streak} assignments={assignments} />

        {/* Top priority callout */}
        {plan.top_priority_text && (
          <div className="bg-blue-950/30 border border-blue-800/30 rounded-lg px-4 py-3">
            <p className="text-xs text-blue-400 font-medium uppercase tracking-wide mb-1">Top Priority</p>
            <p className="text-sm text-slate-200">{plan.top_priority_text}</p>
          </div>
        )}

        {/* Momentum messaging — celebratory beat when commitments are done */}
        {allCommittedDone && <WinCelebration completionPct={completionPct} />}

        {/* Commitment progress bar */}
        <div className="space-y-1.5">
          <div className="flex justify-between text-xs">
            <span className="text-slate-400">Commitments</span>
            <span className={`font-medium ${committedPct === 100 ? 'text-green-400' : committedPct >= 50 ? 'text-blue-400' : 'text-slate-400'}`}>
              {committedDone}/{committed.length}
            </span>
          </div>
          <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-500 ${
                committedPct === 100 ? 'bg-green-500' : 'bg-blue-500'
              }`}
              style={{ width: `${committedPct}%` }}
            />
          </div>
        </div>

        {/* Momentum progress bar (only if momentum items exist) */}
        {momentum.length > 0 && (
          <div className="space-y-1">
            <div className="flex justify-between text-xs">
              <span className="text-slate-500">Momentum</span>
              <span className="text-xs text-amber-500/80 font-medium">
                {momentumDone}/{momentum.length} bonus
              </span>
            </div>
            <div className="h-1.5 bg-slate-700/60 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500 bg-amber-500/70"
                style={{ width: momentum.length > 0 ? `${Math.round((momentumDone / momentum.length) * 100)}%` : '0%' }}
              />
            </div>
          </div>
        )}

        {/* Overall percentage */}
        <div className="text-right">
          <span className={`text-xs font-medium ${completionPct >= 80 ? 'text-green-400' : completionPct >= 50 ? 'text-amber-400' : 'text-slate-500'}`}>
            {completionPct}% overall
          </span>
        </div>

        {/* Commitments section */}
        <div id="today-actions" />
        <div>
          <h3 className="text-xs font-medium text-slate-400 uppercase tracking-wide mb-2">
            Commitments
          </h3>
          <div className="space-y-2">
            {committed.map(action => (
              <ActionItem
                key={action.id}
                action={action}
                onToggle={handleToggleAction}
                onDelete={handleDeleteAction}
                loading={actionLoading === action.id}
              />
            ))}
          </div>
        </div>

        {/* Momentum builders section (only if any exist) */}
        {momentum.length > 0 && (
          <div>
            <h3 className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2">
              Momentum Builders
            </h3>
            <div className="space-y-2">
              {momentum.map(action => (
                <ActionItem
                  key={action.id}
                  action={action}
                  onToggle={handleToggleAction}
                  onDelete={handleDeleteAction}
                  loading={actionLoading === action.id}
                />
              ))}
            </div>
          </div>
        )}

        {/* What the Navigator sees — a personalized observation from your own data */}
        <NavigatorInsight insights={insights} patterns={patterns} currentRead={userState?.current_read} driveLevel={driveLevel} />

        {/* Add action */}
        {addingAction ? (
          <div className="flex gap-2">
            <input
              type="text"
              value={newActionText}
              onChange={(e) => setNewActionText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleAddAction();
                if (e.key === 'Escape') { setAddingAction(false); setNewActionText(''); }
              }}
              placeholder="Add an action..."
              autoFocus
              className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <button
              onClick={handleAddAction}
              disabled={!newActionText.trim()}
              className="bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 text-white rounded-lg px-3 py-2 text-sm transition-colors"
            >
              Add
            </button>
            <button
              onClick={() => { setAddingAction(false); setNewActionText(''); }}
              className="text-slate-500 hover:text-slate-300 px-2 text-sm"
            >
              Cancel
            </button>
          </div>
        ) : (
          <button
            onClick={() => setAddingAction(true)}
            className="w-full border border-dashed border-slate-700/50 hover:border-slate-600 rounded-lg py-2.5 text-xs text-slate-500 hover:text-slate-300 flex items-center justify-center gap-1.5 transition-colors"
          >
            <Plus size={14} /> Add Action
          </button>
        )}

        {/* Quick check-in (a coaching session: API-walled for locked-out users) */}
        {!lockedOut && (
          <button
            onClick={handleStartCheckIn}
            className="w-full bg-emerald-900/30 hover:bg-emerald-900/50 border border-emerald-800/40 text-emerald-300 rounded-lg py-2.5 text-xs font-medium transition-colors flex items-center justify-center gap-2 mt-4"
          >
            <Zap size={14} /> Quick check-in (60-90 sec)
          </button>
        )}

        {/* Deep Rest for this moment: Prime after planning, Rest after review */}
        <div id="deep-rest-card">
          <DeepRestCard kind={momentKind} canCreate={isPaid} className="mt-4" autoPlayWhenReady={false} onSessionsLoaded={loadRestToday} />
        </div>
        {primeNudge.show && (
          <Nudge title="Plan locked. A Prime is on its way." text="If morning sessions are on, a six-minute Prime that rehearses this plan appears on the Deep Rest card in about a minute. Headphones on, then go." onDismiss={primeNudge.dismiss} />
        )}

        {/* Review button */}
        {plan.status !== 'reviewed' && subscription?.tier !== 'free' && (
          <button
            onClick={handleStartReview}
            className="w-full bg-indigo-900/30 hover:bg-indigo-900/50 border border-indigo-800/30 text-indigo-300 rounded-lg py-3 text-sm font-medium transition-colors flex items-center justify-center gap-2 mt-2"
          >
            <Moon size={16} /> Evening Review
          </button>
        )}

        {plan.status === 'reviewed' && plan.review_summary && (
          <div className="bg-slate-800/40 border border-slate-700/40 rounded-lg p-4 mt-2">
            <p className="text-xs text-slate-500 font-medium uppercase tracking-wide mb-1">Review Summary</p>
            <p className="text-sm text-slate-300">{plan.review_summary}</p>
          </div>
        )}

        {limitError && (
          <UpgradePrompt limitError={limitError} onDismiss={() => setLimitError(null)} />
        )}
      </div>
    </div>
  );
}
